<template>
  <div class="booking-confirmation">
    <div class="success-card">
      <div class="success-icon">&#10003;</div>
      <h2 class="success-title">{{ $t('bookingConfirmed') }}</h2>
      <p class="success-sub">{{ detailsCompleted ? $t('bookingDone') : $t('bookingStage1Done') }}</p>
    </div>

    <section v-if="!detailsCompleted" class="confirm-section reminder-section">
      <div class="section-heading">{{ $t('completeDetailsTitle') }}</div>
      <div class="section-body">
        <p class="reminder-copy">{{ $t('completeDetailsHint') }}</p>
        <button type="button" class="btn-complete" @click="goToDetails">{{ $t('completeDetails') }}</button>
      </div>
    </section>

    <section class="confirm-section">
      <div class="section-heading">{{ $t('bookingDetails') }}</div>
      <div class="section-body">
        <div class="detail-row" v-if="data.email"><span class="detail-label">Email</span><span class="detail-value">{{ data.email }}</span></div>
        <div class="detail-block" v-if="facilityName">
          <span class="detail-label">{{ $t('store') }}</span>
          <div class="detail-value-block">
            <div class="facility-name">{{ facilityName }}</div>
            <div v-if="facilityAddress" class="facility-address">{{ facilityAddress }}</div>
          </div>
        </div>
        <div class="detail-row" v-if="data.local_drop_off_date"><span class="detail-label">Drop-off</span><span class="detail-value">{{ data.local_drop_off_date }}</span></div>
        <div class="detail-row" v-if="data.local_pick_up_date"><span class="detail-label">Pick-up</span><span class="detail-value">{{ data.local_pick_up_date }}</span></div>
        <div class="detail-row" v-if="passengerName"><span class="detail-label">Passenger</span><span class="detail-value">{{ passengerName }}</span></div>
        <div class="detail-row" v-if="luggageSummary"><span class="detail-label">Luggage</span><span class="detail-value">{{ luggageSummary }}</span></div>
        <div class="detail-row total" v-if="estimatedTotal != null"><span class="detail-label">Estimated Total</span><span class="detail-value">${{ Number(estimatedTotal).toFixed(2) }}</span></div>
      </div>
    </section>

    <section class="confirm-section">
      <div class="section-heading">{{ $t('qrTitle') }}</div>
      <div class="section-body qr-body">
        <div class="qr-placeholder">
          <div class="qr-box">
            <img v-if="qrDataUrl" :src="qrDataUrl" alt="Booking QR" class="qr-image" />
          </div>
          <p class="qr-hint">{{ $t('showAtCounter') }}</p>
        </div>
      </div>
    </section>

    <div class="actions">
      <button v-if="!detailsCompleted" type="button" class="btn-complete secondary" @click="goToDetails">{{ $t('completeDetails') }}</button>
      <button type="button" class="btn-new" @click="$router.push('/')">{{ $t('createNewBooking') }}</button>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode'
import { resolveStoreName, resolveStoreAddress } from '../constants/storeAddresses'

export default {
  name: 'BookingConfirmation',
  props: { booking: { type: Object, default: null } },
  data () {
    return { qrDataUrl: '' }
  },
  computed: {
    data () { return this.booking || {} },
    tokenValue () { return this.data.token || this.data.booking_id || this.data.id || '' },
    detailsCompleted () { return !!this.data.details_completed },
    facilityName () {
      return resolveStoreName(this.data)
    },
    facilityAddress () {
      return resolveStoreAddress(this.data)
    },
    passengerName () {
      const d = this.data
      if (d.first_name || d.last_name) return [d.first_name, d.last_name].filter(Boolean).join(' ')
      return ''
    },
    estimatedTotal () {
      const ep = this.data.estimated_price
      if (ep && ep.total != null) return ep.total
      return this.data.total != null ? this.data.total : null
    },
    luggageSummary () {
      const items = this.data.luggages || this.data.items
      if (!items || !items.length) return ''
      const counts = {}
      items.forEach(i => { counts[i.size] = (counts[i.size] || 0) + 1 })
      return Object.entries(counts).map(([s, c]) => `${c} ${s}`).join(', ')
    }
  },
  mounted () {
    this.renderQr()
  },
  methods: {
    async renderQr () {
      if (!this.tokenValue) return
      this.qrDataUrl = await QRCode.toDataURL(String(this.tokenValue), { width: 220, margin: 1 })
    },
    goToDetails () {
      if (!this.tokenValue) return
      this.$router.push({ path: '/' + this.tokenValue })
    }
  }
}
</script>

<style scoped>
.booking-confirmation { padding-bottom: 40px; }
.success-card { text-align: center; padding: 32px 20px 28px; margin-bottom: 20px; }
.success-icon { width: 56px; height: 56px; margin: 0 auto 16px; background: #10b981; color: #fff; border-radius: 50%; font-size: 28px; line-height: 56px; font-weight: 700; }
.success-title { font-size: 22px; font-weight: 700; color: #1e336f; margin-bottom: 6px; }
.success-sub { font-size: 15px; color: #6b7280; }
.confirm-section { background: #fff; border-radius: 10px; box-shadow: 0 1px 4px rgba(0,0,0,.06); margin-bottom: 20px; overflow: hidden; }
.reminder-section .section-heading { background: #f59e0b; }
.section-heading { background: #1e336f; color: #fff; font-size: 14px; font-weight: 700; letter-spacing: .5px; padding: 12px 16px; }
.section-body { padding: 16px; }
.reminder-copy { font-size: 14px; line-height: 1.6; color: #374151; margin-bottom: 14px; }
.detail-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px solid #f0f0f5; font-size: 14px; }
.detail-block { padding: 10px 0; border-bottom: 1px solid #f0f0f5; font-size: 14px; }
.detail-block .detail-label { display: block; color: #6b7280; margin-bottom: 6px; }
.detail-value-block { text-align: left; }
.facility-name { font-weight: 600; color: #1e336f; line-height: 1.45; }
.facility-address { margin-top: 6px; font-size: 13px; line-height: 1.55; color: #4b5563; font-weight: 500; }
.detail-row:last-child { border-bottom: none; }
.detail-block:last-child { border-bottom: none; }
.detail-label { color: #6b7280; }
.detail-value { font-weight: 600; color: #1e336f; text-align: right; }
.detail-row.total { border-top: 2px solid #1e336f; border-bottom: none; margin-top: 4px; padding-top: 12px; }
.detail-row.total .detail-value { color: #1e336f; font-size: 18px; }
.qr-body { text-align: center; padding: 24px 16px; }
.qr-box { width: 140px; height: 140px; margin: 0 auto 14px; background: #fff; border: 2px solid #e8ecf1; border-radius: 8px; padding: 8px; }
.qr-image { width: 100%; height: 100%; }
.qr-hint { font-size: 13px; color: #6b7280; }
.actions { display: flex; flex-direction: column; gap: 12px; align-items: center; margin-top: 24px; }
.btn-complete { font-size: 15px; font-weight: 700; color: #fff; background: #1e336f; border: none; border-radius: 10px; padding: 12px 32px; cursor: pointer; }
.btn-complete.secondary { background: #fff; color: #1e336f; border: 2px solid #1e336f; }
.btn-new { font-size: 15px; font-weight: 600; color: #1e336f; background: #fff; border: 2px solid #1e336f; border-radius: 10px; padding: 12px 32px; cursor: pointer; }
.btn-complete:hover, .btn-new:hover { opacity: .92; }
</style>



// WEBPACK FOOTER //
// src/components/BookingConfirmation.vue